import {
  AdminFinanceSummarySchema,
  BiddingModeKindSchema,
  BuyingPowerStateSchema,
  CarInvoicePreviewSchema,
  DepositMultiplierSchema,
  FinanceClientSchema,
  FinanceTransactionSchema,
  InvoiceSchema,
  PricingRuleSetSchema,
  RevenueMonthSchema,
  TopUpRequestSchema,
  type AdminFinanceSummary,
  type BiddingModeKind,
  type BuyingPowerState,
  type CarInvoicePreview,
  type DepositMultiplier,
  type FinanceClient,
  type FinancePeriod,
  type Invoice,
  type InvoiceKind,
  type InvoiceLine,
  type LedgerMethod,
  type PricingRuleSet,
  type RevenueMonth,
  type TopUpRequest,
} from '@carsfinders/contracts';

import { ApiError, apiFetch } from '../../lib/api';
import type { JournalPage } from './types';

/**
 * The «Фінанси» screen's data layer — one function per endpoint, nothing else.
 *
 * ── Five queries, not one snapshot ──────────────────────────────────────────
 * Until this phase every figure came from `fetchFinanceSnapshot()`, a single
 * promise over `finance-fixtures.ts`. The backend has no such endpoint and
 * should not: the reads are independent aggregates with different costs and
 * different refresh rates, and folding them into one request would make the
 * whole screen fail whenever the slowest of them did. Each panel now owns its
 * query, so a broken journal leaves the balances table standing.
 *
 *   GET  /api/admin/finance/clients                 -> FinanceClient[]
 *   GET  /api/admin/finance/summary?period=         -> AdminFinanceSummary
 *   GET  /api/admin/finance/journal?limit=&cursor=  -> { entries, nextCursor }
 *   GET  /api/admin/finance/revenue?months=         -> RevenueMonth[]
 *   GET  /api/admin/finance/invoices?userId=        -> Invoice[]
 *   GET  /api/admin/finance/invoices/car-preview?dealId=&userId= -> CarInvoicePreview | typed refusal
 *   POST /api/admin/finance/invoices                -> Invoice | typed refusal
 *   GET  /api/admin/finance/deposits                -> DepositsOverviewRow[]
 *   POST /api/admin/users/:userId/deposit/remind    -> {success, data: {queued}}
 *   POST /api/admin/users/:userId/deposit/refund    -> {success, data: AppliedMovement}
 *
 * ── PARSED, never cast ──────────────────────────────────────────────────────
 * `apiFetch<T>` is a bare cast over `response.json()`, and JSON HAS NO DATE.
 * Every shape on this surface carries at least one `z.coerce.date()` field
 * (`dueAt`, `issuedAt`, `at`, `computedAt`, `nextDueAt`), which arrives as a
 * string. A cast would hand the screen a `string` typed as `Date`, and
 * `deriveInvoiceStatus` — which compares `dueAt.getTime()` against now — would
 * throw on the first row. The tariffs screen was blanked by exactly that
 * (`features/pricing/pricing-preview.ts fetchCurrentRules`). So every response
 * goes through its contract schema, which coerces the dates and refuses a shape
 * the screen cannot render.
 *
 * The schemas are reached through the objects `@carsfinders/contracts` exports
 * (`Schema.array()`, `Schema.parse`) rather than by importing `zod` here: the
 * admin SPA does not depend on zod and must not grow a phantom one.
 *
 * ── The error envelope is FORKED, and the fork is load-bearing ──────────────
 * `apps/api` installs no global exception filter. A 401/403 is raised by the
 * guard ladder and never reaches the controller's `catch`, so it answers Nest's
 * default `{ statusCode, message, error: 'Unauthorized' }` — a STRING on
 * `error`. Everything a HANDLER produces answers
 * `{ success: false, error: { message, type, statusCode, timestamp, code? } }`
 * — an OBJECT on the same key. A reader that goes straight for `error.code`
 * gets `undefined` on the first, and one that renders `error` as text gets
 * `[object Object]` out of the second. {@link financeErrorCode} therefore
 * branches on the HTTP STATUS first and only reads `error.code` off a body it
 * already knows came from a handler.
 */

/* ────────────────────────────── Reads ────────────────────────────────────── */

/** Every client's debt position, one request. */
export async function fetchFinanceClients(): Promise<FinanceClient[]> {
  const payload = await apiFetch<unknown>('admin/finance/clients');
  return FinanceClientSchema.array().parse(payload);
}

export const FINANCE_CLIENTS_QUERY_KEY = ['admin', 'finance', 'clients'] as const;

/** The headline figures for one window. */
export async function fetchFinanceSummary(period: FinancePeriod): Promise<AdminFinanceSummary> {
  const payload = await apiFetch<unknown>('admin/finance/summary', {
    searchParams: { period },
  });
  return AdminFinanceSummarySchema.parse(payload);
}

export const financeSummaryQueryKey = (period: FinancePeriod) =>
  ['admin', 'finance', 'summary', period] as const;

/** How many months of revenue the chart draws. The API caps this at 24. */
export const REVENUE_MONTHS = 6;

/** The three revenue streams per calendar month, oldest bar first. */
export async function fetchRevenue(months: number = REVENUE_MONTHS): Promise<RevenueMonth[]> {
  const payload = await apiFetch<unknown>('admin/finance/revenue', {
    searchParams: { months: String(months) },
  });
  return RevenueMonthSchema.array().parse(payload);
}

export const revenueQueryKey = (months: number = REVENUE_MONTHS) =>
  ['admin', 'finance', 'revenue', months] as const;

/** How many journal rows the screen's table shows. */
export const JOURNAL_PAGE_SIZE = 20;

/** One page of the money journal, newest first. */
export async function fetchJournalPage(
  limit: number = JOURNAL_PAGE_SIZE,
  cursor?: string,
): Promise<JournalPage> {
  const payload = await apiFetch<{ entries?: unknown; nextCursor?: unknown }>(
    'admin/finance/journal',
    { searchParams: { limit: String(limit), cursor } },
  );
  const nextCursor = payload?.nextCursor;
  return {
    entries: FinanceTransactionSchema.array().parse(payload?.entries),
    nextCursor: typeof nextCursor === 'string' ? nextCursor : null,
  };
}

export const journalQueryKey = (limit: number = JOURNAL_PAGE_SIZE) =>
  ['admin', 'finance', 'journal', limit] as const;

/**
 * One client's invoices, newest issue date first.
 *
 * NOT the same set as the client row's own `invoices`, and the screen must not
 * present them as reconciled views of one another: the rollup folds stored
 * invoices together with provisional rows derived from invoiced deals, while
 * this endpoint answers the stored collection. Two readers of the same
 * externally-written documents can legitimately disagree, and the drawer is the
 * one that under-reports.
 */
export async function fetchClientInvoices(userId: string): Promise<Invoice[]> {
  const payload = await apiFetch<unknown>('admin/finance/invoices', {
    searchParams: { userId },
  });
  return InvoiceSchema.array().parse(payload);
}

export const clientInvoicesQueryKey = (userId: string) =>
  ['admin', 'finance', 'invoices', userId] as const;

/**
 * What issuing a car invoice for `dealId` would write — the server's own line
 * builder and `prepaymentFor`, nothing written. Refused with the issue's own
 * typed codes (`DEAL_NOT_INVOICEABLE`, `DEAL_ALREADY_INVOICED`, …).
 */
export async function fetchCarInvoicePreview(
  dealId: string,
  userId: string,
): Promise<CarInvoicePreview> {
  const payload = await apiFetch<unknown>('admin/finance/invoices/car-preview', {
    searchParams: { dealId, userId },
  });
  return CarInvoicePreviewSchema.parse(payload);
}

export const carInvoicePreviewQueryKey = (dealId: string, userId: string) =>
  ['admin', 'finance', 'invoices', 'car-preview', dealId, userId] as const;

/* ────────────────────────────── The one write ────────────────────────────── */

/** What `POST /api/admin/finance/invoices` accepts (`invoices.service.ts`). */
export interface CreateInvoiceBody {
  userId: string;
  kind: InvoiceKind;
  /** Required for `kind: 'car'`, refused otherwise. */
  dealId?: string | null;
  /** The agreed deadline, ISO-8601. The API refuses one in the past. */
  dueAt: string;
  /** Required for every kind EXCEPT `car`, which is priced server-side. */
  lines?: InvoiceLine[];
  /*
   * No line corrections. Car-invoice lines are fixed and system-calculated
   * (owner, 2026-09-28); the API refuses a body carrying `lineOverrides`.
   */
  /*
   * No deposit field. Since phase 6 the server applies the client's deposit to
   * a car invoice as a prepayment (`prepaymentFor`) inside the issuing
   * transaction; the old offset field is ignored there and is not sent.
   */
}

/**
 * Issue one invoice.
 *
 * ⛔ INERT while `PAYMENTS_ENABLED` is off: the API answers 409 with
 * `code: 'PAYMENTS_DISABLED'` and writes nothing at all. That is a refusal with
 * a reason, not a failure, and the drawer renders it as such — see
 * {@link financeErrorCode}.
 */
export async function createInvoice(body: CreateInvoiceBody): Promise<Invoice> {
  const payload = await apiFetch<unknown>('admin/finance/invoices', {
    method: 'POST',
    body,
  });
  return InvoiceSchema.parse(payload);
}

/* ─────────────────────────── Deposits & bidding modes ────────────────────── */

/**
 * One row of «Депозити й режими» — `DepositsOverviewRow` as
 * `apps/api/src/modules/admin-finance/deposits-overview.service.ts` serves it.
 *
 * Declared here because contracts does not model the row (it is the API's own
 * read model over `users` + `bids`); every ENUM inside it is the contract's,
 * and {@link parseDepositsRow} validates each against its schema. `state` and
 * the cap maths are the server's `resolveBuyingPower` / `buyingPowerState`
 * answers — this screen never re-derives them.
 */
export interface DepositsOverviewRow {
  userId: string;
  name: string | null;
  email: string | null;
  /** Null for a client not yet backfilled (`users.bidding` absent). */
  mode: BiddingModeKind | null;
  legacy: boolean;
  multiplier: DepositMultiplier | null;
  bidLimit: number | null;
  depositChf: number;
  committedChf: number;
  /** Deposit mode only: deposit × multiplier. */
  capChf: number | null;
  /** Deposit mode only: max(0, cap − committed). */
  freeChf: number | null;
  activeBidCount: number;
  /** Null for a legacy client with no refund request. */
  state: BuyingPowerState | null;
  refundRequestedAt: Date | null;
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/**
 * Parse one wire row. Throws on a row the screen cannot render (no `userId`,
 * an enum value contracts does not know) rather than guessing — JSON has no
 * `Date`, so `refundRequestedAt` is coerced here as well.
 */
export function parseDepositsRow(raw: unknown): DepositsOverviewRow {
  if (typeof raw !== 'object' || raw === null) throw new Error('deposits row is not an object');
  const row = raw as Record<string, unknown>;
  if (typeof row.userId !== 'string' || row.userId === '') {
    throw new Error('deposits row carries no userId');
  }
  const refundAt = row.refundRequestedAt;
  const refundDate =
    typeof refundAt === 'string' || refundAt instanceof Date ? new Date(refundAt) : null;
  return {
    userId: row.userId,
    name: stringOrNull(row.name),
    email: stringOrNull(row.email),
    mode: BiddingModeKindSchema.nullable().parse(row.mode ?? null),
    legacy: row.legacy === true,
    multiplier: DepositMultiplierSchema.nullable().parse(row.multiplier ?? null),
    bidLimit: finiteOrNull(row.bidLimit),
    depositChf: finiteOr(row.depositChf, 0),
    committedChf: finiteOr(row.committedChf, 0),
    capChf: finiteOrNull(row.capChf),
    freeChf: finiteOrNull(row.freeChf),
    activeBidCount: finiteOr(row.activeBidCount, 0),
    state: BuyingPowerStateSchema.nullable().parse(row.state ?? null),
    refundRequestedAt: refundDate && !Number.isNaN(refundDate.getTime()) ? refundDate : null,
  };
}

/** Every client with a bidding mode, a deposit or a pending refund request. */
export async function fetchDeposits(): Promise<DepositsOverviewRow[]> {
  const payload = await apiFetch<unknown>('admin/finance/deposits');
  if (!Array.isArray(payload)) throw new Error('deposits payload is not an array');
  return payload.map(parseDepositsRow);
}

export const DEPOSITS_QUERY_KEY = ['admin', 'finance', 'deposits'] as const;

/**
 * «Нагадати поповнити» — queues a `deposit.topup_reminder` (202). Delivery
 * rides notify-dispatch, which is off in prod until the owner enables it
 * (owner gate G7): the call succeeds and the message waits in the outbox.
 */
export async function remindDepositTopUp(userId: string): Promise<void> {
  await apiFetch<unknown>(`admin/users/${encodeURIComponent(userId)}/deposit/remind`, {
    method: 'POST',
  });
}

/**
 * «Повернути CHF …» — records the refund of the WHOLE deposit (one ledger
 * `refund` row). NOT behind `PAYMENTS_ENABLED`: it books money the operator
 * moves outside the platform (owner gate G4). 409 `REFUND_NOT_ALLOWED` while
 * an active bid or an unpaid invoice still depends on the deposit.
 */
export async function refundDeposit(userId: string): Promise<void> {
  await apiFetch<unknown>(`admin/users/${encodeURIComponent(userId)}/deposit/refund`, {
    method: 'POST',
  });
}

/* ────────────────────────────── Top-up requests desk ─────────────────────── */

/**
 * One request in the admin queue — `AdminTopUpRequest` as
 * `apps/api/src/modules/payments/topup-requests.service.ts` serves it
 * (`TopUpRequest` + the client's display name). Not modelled in contracts for
 * the same reason `DepositsOverviewRow` above is not: it is this admin
 * surface's own read model, one field wider than the shared `TopUpRequest`.
 */
export interface AdminTopUpRequest extends TopUpRequest {
  clientName: string | null;
}

function parseAdminTopUpRequest(raw: unknown): AdminTopUpRequest {
  const request = TopUpRequestSchema.parse(raw);
  const clientName = (raw as Record<string, unknown> | null)?.clientName;
  return { ...request, clientName: typeof clientName === 'string' ? clientName : null };
}

export type TopUpRequestListStatus = 'open' | 'all';

/** The manager's queue: `open` (default) or every request ever made. */
export async function fetchTopUpRequests(
  status: TopUpRequestListStatus = 'open',
): Promise<AdminTopUpRequest[]> {
  const payload = await apiFetch<{ data?: unknown }>('admin/finance/topup-requests', {
    searchParams: { status },
  });
  const rows = payload?.data;
  if (!Array.isArray(rows)) throw new Error('topup-requests payload is not an array');
  return rows.map(parseAdminTopUpRequest);
}

export const adminTopUpRequestsQueryKey = (status: TopUpRequestListStatus = 'open') =>
  ['admin-topup-requests', status] as const;

/**
 * What `POST …/topup-requests/:id/confirm` accepts (`TopUpConfirmSchema`).
 * `amountChf` defaults to the requested amount server-side when omitted; a
 * `cash` confirmation without `receiptRef` is refused by the SAME schema on
 * the server — the dialog mirrors that one rule client-side so the operator
 * sees it before the round trip, not after.
 */
export interface ConfirmTopUpBody {
  amountChf?: number;
  method: LedgerMethod;
  receiptRef?: string;
}

/**
 * Confirm one request: the manager's record of how the money actually
 * arrived. 409 `TOPUP_NOT_OPEN` when it was already confirmed/rejected/
 * cancelled — read with {@link refusalCode}, same fork as every other typed
 * refusal on this surface.
 */
export async function confirmTopUpRequest(
  id: string,
  body: ConfirmTopUpBody,
): Promise<TopUpRequest> {
  const payload = await apiFetch<{ data?: { request?: unknown } }>(
    `admin/finance/topup-requests/${encodeURIComponent(id)}/confirm`,
    { method: 'POST', body },
  );
  return TopUpRequestSchema.parse(payload?.data?.request);
}

/** What `POST …/topup-requests/:id/reject` accepts (`TopUpRejectSchema`). */
export interface RejectTopUpBody {
  reason: string;
}

export async function rejectTopUpRequest(id: string, body: RejectTopUpBody): Promise<TopUpRequest> {
  const payload = await apiFetch<{ data?: unknown }>(
    `admin/finance/topup-requests/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body },
  );
  return TopUpRequestSchema.parse(payload?.data);
}

/**
 * «Поповнити вручну» — the direct top-up an operator records without a
 * client-submitted request. Since the client-finances rethink there is ONE
 * way in for money a client paid, `POST /admin/users/:userId/payments`: this
 * is that call with the whole CHF amount allocated to the deposit.
 *
 * `paymentId` is the idempotency key, minted ONCE per dialog open by the
 * caller — a retry after a failure (or a double click) sends the same key and
 * the server answers the recorded result instead of crediting twice. A `cash`
 * entry needs its cash-desk receipt (`RECEIPT_REQUIRED` server-side); the
 * dialog mirrors that rule before the round trip.
 */
export interface DirectTopUpBody {
  paymentId: string;
  amountChf: number;
  method: LedgerMethod;
  receiptRef?: string;
}

/**
 * Record a top-up outside the request flow. The response carries nothing the
 * deposits table cannot re-derive by refetching, so it is discarded like
 * {@link remindDepositTopUp}.
 */
export async function createDirectTopUp(userId: string, body: DirectTopUpBody): Promise<void> {
  const { paymentId, amountChf, method, receiptRef } = body;
  await apiFetch<unknown>(`admin/users/${encodeURIComponent(userId)}/payments`, {
    method: 'POST',
    body: {
      paymentId,
      amount: amountChf,
      currency: 'CHF',
      receivedAt: new Date().toISOString(),
      method,
      ...(receiptRef ? { reference: receiptRef } : {}),
      allocations: [{ to: 'deposit', amountChf }],
    },
  });
}

/* ────────────────────────────── Error reading ────────────────────────────── */

/** The refusal codes `invoices.service.ts` can answer with. */
export const INVOICE_ERROR_CODES = [
  'PAYMENTS_DISABLED',
  'INVOICE_NUMBER_TAKEN',
  'DEAL_NOT_FOUND',
  'DEAL_NOT_PRICEABLE',
  'PRICING_RULES_UNAVAILABLE',
  'DEPOSIT_OFFSET_EXCEEDS_DEPOSIT',
  'DEAL_ALREADY_INVOICED',
  'DEAL_NOT_INVOICEABLE',
  'PREPAYMENT_ALREADY_APPLIED',
  'PREPAYMENT_REFUSED',
  'CAR_INVOICE_TOTAL_NOT_POSITIVE',
] as const;
export type InvoiceErrorCode = (typeof INVOICE_ERROR_CODES)[number];

/**
 * The `error` OBJECT a handler wrote, or null for every other body shape.
 *
 * Returns null on 401/403 WITHOUT looking at the body. Those come from the
 * guard ladder rather than the handler and carry a STRING on `error`; reading
 * `.code` off a string yields `undefined` rather than throwing, which is how
 * "you are not signed in" would silently become "unknown failure".
 */
function handlerError(error: unknown): { message?: unknown; code?: unknown } | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 401 || error.status === 403) return null;
  const body = error.body;
  if (typeof body !== 'object' || body === null) return null;
  const envelope = (body as { error?: unknown }).error;
  if (typeof envelope !== 'object' || envelope === null) return null;
  return envelope as { message?: unknown; code?: unknown };
}

/** The typed refusal code behind an `ApiError`, or `null` when there is none. */
export function financeErrorCode(error: unknown): InvoiceErrorCode | null {
  const code = handlerError(error)?.code;
  return typeof code === 'string' && (INVOICE_ERROR_CODES as readonly string[]).includes(code)
    ? (code as InvoiceErrorCode)
    : null;
}

/**
 * ANY typed refusal code a handler sent (deposit routes included), or null.
 * Same status-first fork as {@link financeErrorCode}, without the invoice
 * allow-list — the deposit routes answer their own codes
 * (`REFUND_NOT_ALLOWED`, `USER_NOT_FOUND`, …).
 */
export function refusalCode(error: unknown): string | null {
  const code = handlerError(error)?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * The operator-facing sentence a handler sent, when it sent one.
 *
 * `apiFetch` only lifts `error` into `ApiError.message` when it is a STRING,
 * which on this surface it never is — the envelope puts an object there — so
 * the sentence has to be read back out of the body.
 */
export function financeErrorMessage(error: unknown): string | null {
  const message = handlerError(error)?.message;
  return typeof message === 'string' ? message : null;
}

/**
 * True when the operator is anonymous or lacks the admin role.
 *
 * The one branch that must be taken before any body is read, for the reason on
 * {@link handlerError}: on these two statuses the body is not the finance
 * envelope at all, and the answer is the shared access-denied screen.
 */
export function isAccessError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/* ────────────────────────────── Pricing rules ────────────────────────────── */

/**
 * The pricing rule set in force — THE tariff the invoice drawer quotes.
 *
 * PARSED for the same reason as everything above: `customs.validFrom` arrives
 * as a string while the type claims a `Date`, and parsing here is also what
 * applies the legacy-document migrations.
 */
export function fetchPricingRules(): Promise<PricingRuleSet> {
  return apiFetch<unknown>('pricing/rules/current').then((payload) =>
    PricingRuleSetSchema.parse(payload),
  );
}

/** Query key for the rule set — shared shape with the tariffs screen's fetch. */
export const PRICING_RULES_QUERY_KEY = ['admin', 'pricing', 'rules', 'current'] as const;
